import { useEffect, useMemo, useState } from 'react';
import type { OrderCancellationReason } from '@skupreme/api-contracts/core';
import { StatusText } from '@components/StatusIndicator';
import ListService from '@utility/services/list';
import { DateUtility } from '@utility/date';
import {
  ALL_DROPDOWN_OPTION,
  ErrorMessageConstant,
  InvoiceStatus,
} from '@utility/constants/common';
import OrdersService from '@utility/services/order';
import { dropdownOptionsIncludeFlag } from '@elements/Flag';
import { metadataHook, orgMetadataHook } from './metadata';
import type {
  IMarketplaceMeta,
  MarketplaceListItem,
} from 'types/hook/marketplace';
import { getMarketplaceList } from '@utility/services/marketplace';

export type { MarketplaceListItem };

export const orderStatusListHook = () => {
  const { metadata, loading } = metadataHook();
  const data = useMemo(() => {
    const temp = [];
    // @ts-expect-error - TS2769: No overload matches this call.
    if (Object.keys(metadata).length > 0) {
      // @ts-expect-error - TS18048: 'metadata' is possibly 'undefined'.
      const statues = metadata.orderStatus;
      Object.keys(statues || {}).forEach(key => {
        // @ts-expect-error - TS2345: Argument of type '{ key: string; text: any; value: string; content: JSX.Element;...
        temp.push({
          key,
          text: statues[key].text,
          value: key,
          /* @ts-expect-error - TS2739: Type '{ tableKey: string; status: string; }' is missing the ... */
          content: <StatusText tableKey="orderStatus" status={key} />,
        });
      });
    }
    return temp;
  }, [metadata]);

  return { data, loading };
};

export const shipmentStatusListHook = (all = false) => {
  const { metadata, loading } = metadataHook();
  const data = useMemo(() => {
    const temp = [];
    // @ts-expect-error - TS2769: No overload matches this call.
    if (Object.keys(metadata).length > 0) {
      // @ts-expect-error - TS18048: 'metadata' is possibly 'undefined'.
      const statues = metadata.shipmentStatus;
      Object.keys(statues || {}).forEach(key => {
        const obj = {
          key,
          text: statues[key].text,
          value: key,
        };
        // @ts-expect-error - TS2345: Argument of type '{ key: string; text: any; value: string; }' is not assignable ...
        temp.push(obj);
      });
    }
    if (all) {
      // @ts-expect-error - TS2345: Argument of type '{ key: string; text: string; value: string; }' is not assignab...
      temp.unshift({
        key: '-1',
        text: 'View All',
        value: '',
      });
    }
    return temp;
  }, [metadata]);

  return { data, loading };
};

export const marketplaceListHook = (
  includeManual = true,
  includeUnconnected = false,
  enabled = true,
): { data: MarketplaceListItem[]; loading: boolean } => {
  const { marketplaces, loading } = orgMetadataHook(enabled) as {
    marketplaces: IMarketplaceMeta[];
    loading: boolean;
  };

  const marketplaceList = useMemo(
    () => getMarketplaceList(marketplaces, includeManual, includeUnconnected),
    [marketplaces, includeManual, includeUnconnected],
  );

  return { data: marketplaceList, loading };
};

export const purchaseOrderStatusListHook = (all = false) => {
  const { metadata, loading } = metadataHook();
  const data = useMemo(() => {
    const temp = [];
    // @ts-expect-error - TS2769: No overload matches this call.
    if (Object.keys(metadata).length > 0) {
      // @ts-expect-error - TS18048: 'metadata' is possibly 'undefined'.
      const statues = metadata.purchaseOrderStatus;
      Object.keys(statues || {}).forEach(key => {
        const obj = {
          key,
          text: statues[key].text,
          value: key,
        };
        // @ts-expect-error - TS2345: Argument of type '{ key: string; text: any; value: string; }' is not assignable ...
        temp.push(obj);
      });
    }
    if (all) {
      // @ts-expect-error - TS2345: Argument of type '{ key: string; text: string; value: string; }' is not assignab...
      temp.unshift({
        key: '-1',
        text: 'View All',
        value: '',
      });
    }
    return temp;
  }, [metadata]);

  return { data, loading };
};

export const invoiceStatusListHook = (all = false) => {
  const { metadata, loading } = metadataHook();
  const defaultOptions = [];
  const data = useMemo(() => {
    const temp = [];
    // @ts-expect-error - TS2769: No overload matches this call.
    if (Object.keys(metadata).length > 0) {
      // @ts-expect-error - TS18048: 'metadata' is possibly 'undefined'.
      const statues = metadata.purchaseOrderStatus;
      Object.keys(statues || {}).forEach(key => {
        const obj = {
          key,
          label: statues[key].text,
          value: key,
          name: key,
        };
        // @ts-expect-error - TS2345: Argument of type '{ key: string; label: any; value: string; name: string; }' is ...
        temp.push(obj);
        if (
          [
            InvoiceStatus.open,
            InvoiceStatus.pendingAction,
            InvoiceStatus.pendingPayment,
            InvoiceStatus.inProduction,
            InvoiceStatus.readyForShipment,
            // @ts-expect-error - TS2345: Argument of type 'string' is not assignable to parameter of type '"open" | "pend...
          ].includes(key)
        ) {
          // @ts-expect-error - TS2345: Argument of type '{ key: string; label: any; value: string; name: string; }' is ...
          defaultOptions.push(obj);
        }
      });
    }
    if (all) {
      // @ts-expect-error - TS2345: Argument of type '{ readonly key: -1; readonly name: "All"; readonly text: "All"...
      temp.unshift(ALL_DROPDOWN_OPTION);
    }
    return { options: temp, defaultOptions };
  }, [metadata]);

  return { data, loading };
};

export const countryListHook = inOrder => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    try {
      setLoading(true);
      let result = await ListService.country();
      if (inOrder) {
        result = result.sort((a, b) => (a.order || 500) - (b.order || 500));
      }
      const list = (result || []).map(x => ({
        key: x.code,
        text: x.name,
        value: x.code,
        flag: x.code.toLowerCase(),
      }));
      // @ts-expect-error - TS2345: Argument of type '{ image: { src: string; }; flag: null; key?: string | number |...
      setData(dropdownOptionsIncludeFlag(list));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  return { data, loading };
};

export const timezoneListHook = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    try {
      setLoading(true);
      const result = await ListService.timezone();
      const list = (result || []).map(x => ({
        text: x.text,
        value: x.value,
      }));
      setData(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  return { data, loading };
};

export const hourListHook = () => {
  const [data, setData] = useState([]);

  const fetch = async () => {
    const result = DateUtility.hoursList();
    const list = (result || []).map(x => ({
      key: x,
      text: x,
      value: x,
    }));
    // @ts-expect-error - TS2345: Argument of type '{ key: string; text: string; value: string; }[]' is not assign...
    setData(list);
  };

  useEffect(() => {
    fetch();
  }, []);

  return { data };
};

export const itemHSCodeListHook = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    try {
      setLoading(true);
      const result = await ListService.itemHSCode();
      const list = (result.data || []).map(x => ({
        key: x.class_name,
        text: x.class_name,
        value: x.class_name,
        marketplace_region_id: x.marketplace_region_id,
      }));
      setData(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  return { data, loading };
};

type CancelReasonOption = {
  key: OrderCancellationReason;
  text: OrderCancellationReason;
  value: OrderCancellationReason;
};

export const cancelReasonsListHook = () => {
  const [data, setData] = useState<CancelReasonOption[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await OrdersService.orderCancellationReasonsList();
      const list = (result.data || []).map(x => ({
        key: x,
        text: x,
        value: x,
      }));
      setData(list);
    } catch (error) {
      setError(error.message || ErrorMessageConstant.defaultAPIError);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, []);
  return { data, loading, error };
};
